import BackButton from "components/Shared/BackButton"
import DetailsTitle from "components/Shared/DetailsTitle"
import Spinners from "components/Shared/Spinners"
import { usePermissions } from "hooks"
import React, { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import { getJobTitlesList } from "store/actions/commonAction"
import {
  getAssessmentDetails,
  getInterviewQuestions,
  submitInterviewAssessment,
} from "store/actions/interviewsAction"
import { getJobApplicationDetails } from "store/actions/jobApplicationAction"
import { showAlert } from "store/reducers/alertReducer"
import CandidateDetails from "./CandidateDetails"
import { getLocalizedName } from "components/Common/Utils"

const ViewAssessment = ({}) => {
  const {
    gettingAssessmentDetails,
    assessmentDetails,
    totalAllowTime,
    isLoading,
    questions,
  } = useSelector(state => state.Interviews)
  const { jobTitlesList } = useSelector(state => state.Common)

  const {
    t,
    i18n: { language: localizeLanguage },
  } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(location.search)
  const uuid = searchParams.get("uuid") // User UUID

  const interviewUuid = searchParams.get("interviewUuid")
  const { jobApplicationDetails } = useSelector(state => state.JobApplication)
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  useEffect(() => {
    fetchAssessmentDetails()
    fetchUserDetails()
  }, [])

  const fetchUserDetails = () => {
    dispatch(getJobApplicationDetails(uuid))
  }

  const { jobIds, showAll, permissions } = usePermissions(
    "interview_evaluation"
  )

  const [grade, setGrade] = useState(null)
  const [timeLeft, setTimeLeft] = useState(null) // 2 minutes in seconds
  const [activeButtons, setActiveButtons] = useState({}) // Track selected options
  const [jobNominated, setJobNominated] = useState(null)
  const [proposedPosition, setProposedPosition] = useState(null)
  const [suitableForJob, setSuitableForJob] = useState(null) // Track suitability for job
  const [leadershipPosition, setLeadershipPosition] = useState(null)
  const [reason, setReason] = useState(null)
  const [review, setReview] = useState("") // Track review
  const [is_profile_accepted, setIs_profile_accepted] = useState(null)
  const [
    would_you_like_work_in_trial_period,
    setWould_you_like_work_in_trial_period,
  ] = useState(null)
  const [is_eligible_for_promotion, setEligible_for_promoted] = useState(null)
  const [promotion_job_title_id, setPromotion_job_title_id] = useState(null)

  const [language, setLanguage] = useState(null)

  const fetchAssessmentDetails = () => {
    dispatch(getAssessmentDetails(interviewUuid))
  }

  useEffect(() => {
    // Populate form with existing data if we're viewing an assessment
    if (assessmentDetails && assessmentDetails.assessments) {
      // Set active buttons based on assessment data
      const activeButtonsMap = {}
      assessmentDetails?.assessments?.forEach(category => {
        category?.questions?.forEach(question => {
          if (question.option_id) {
            activeButtonsMap[question.question_id] = question.option_id
          }
        })
      })
      setActiveButtons(activeButtonsMap)
      setSuitableForJob(
        assessmentDetails?.is_candidate_suitable_for_job ? "Yes" : "No"
      )
      setReview(assessmentDetails?.review)
      setLeadershipPosition(
        assessmentDetails?.is_candidate_nominated_for_leadership ? "Yes" : "No"
      )
      setReason(assessmentDetails?.review_type)
      setProposedPosition(assessmentDetails?.job_title_id)
      setTimeLeft(assessmentDetails?.time_taken)
      setIs_profile_accepted(assessmentDetails?.is_profile_accepted)
      setGrade(assessmentDetails?.obtained_score)
      setWould_you_like_work_in_trial_period(
        assessmentDetails?.would_you_like_work_in_trial_period
      )
      setLanguage(assessmentDetails?.language)
      setJobNominated(assessmentDetails?.nominated_job)
      setProposedPosition(assessmentDetails?.job_title?.id)
      setSuitableForJob(
        assessmentDetails?.is_candidate_suitable_for_job ? "Yes" : "No"
      )
      setEligible_for_promoted(
        assessmentDetails?.is_eligible_for_promotion ? "Yes" : "No"
      )
      setPromotion_job_title_id(assessmentDetails?.promotion_job_title_id)
    }
  }, [assessmentDetails])

  useEffect(() => {
    getAllJobTitles()
  }, [])

  const getAllJobTitles = () => {
    dispatch(getJobTitlesList())
  }

  // Format time for display
  const formatTime = seconds => {
    const isNegative = seconds < 0 // Check if time is negative
    const absoluteSeconds = Math.abs(seconds) // Work with absolute value for calculation
    const mins = Math.floor(absoluteSeconds / 60)
    const secs = absoluteSeconds % 60
    return `${isNegative ? "-" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`
  }

  const selectedJob = jobTitlesList.find(
    item => item.id === Number(proposedPosition)
  )

  return (
    <div className="page-content">
      {gettingAssessmentDetails ? (
        <Spinners />
      ) : (
        <Container fluid>
          <div className="g_start mb-4">
            <i
              className="bx bx-arrow-back me-1 fs-5 cursor-pointer"
              onClick={() => navigate("/interview-appointments")}
            ></i>
            <h4 className="card-title mb-0">{t("Assessment Details")}</h4>
          </div>
          <div className="interview_container">
            <div className="left">
              <div className={`timer ${timeLeft > 0 ? "" : "time_left"}`}>
                {timeLeft ? formatTime(timeLeft) : ""}
              </div>
              <div className="final_grade">
                <h6>{t("Final Grade")}</h6>
                <h5 className={`${grade >= 70 ? "text-success" : ""}`}>
                  {grade} <span>/100</span>
                </h5>
              </div>
              {/* Candidate info and other UI elements */}
              <div className="candidate-info">
                <div className="pic_area">
                  <div className="pic">
                    <img
                      className="candidate-photo img-fluid"
                      src={assessmentDetails?.user?.profile_picture}
                      alt={t("candidate-photo")}
                    />
                  </div>

                  {/* <button className="theme_btn">Full Size</button> */}
                </div>
                <Table bordered responsive>
                  <tbody>
                    <tr>
                      <th>{t("Id")}</th>
                      <td>{assessmentDetails?.user?.id}</td>
                    </tr>
                    <tr>
                      <th>{t("Name")}</th>
                      <td>{assessmentDetails?.user?.name}</td>
                    </tr>
                    <tr>
                      <th>{t("Phone Number")}</th>
                      <td>
                        <p className="ltr fit_content">
                          {assessmentDetails?.user?.phone}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <th>{t("Email")}</th>
                      <td>{assessmentDetails?.user?.email}</td>
                    </tr>
                    <tr>
                      <th>
                        <label
                          htmlFor="profileAccepted"
                          className="fw-bold d-block"
                          style={{ maxWidth: 100 }}
                        >
                          {t("Is the candidate photo acceptable?")}
                        </label>
                      </th>
                      <td>
                        <div className="ms-3 mt-2">
                          <div className="d-flex gap-2">
                            <input
                              type="radio"
                              id="profileAcceptedYes"
                              name="profileAccepted"
                              value="true"
                              checked={is_profile_accepted === true}
                              style={{ height: "unset" }}
                            />
                            <label
                              htmlFor="profileAcceptedYes"
                              className="mb-0"
                            >
                              {t("Yes")}
                            </label>

                            <input
                              type="radio"
                              id="profileAcceptedNo"
                              name="profileAccepted"
                              value="false"
                              checked={is_profile_accepted === false}
                              className=""
                              style={{ height: "unset" }}
                            />
                            <label htmlFor="profileAcceptedNo" className="mb-0">
                              {t("No")}
                            </label>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>{t("ID Number")}</th>
                      <td>{assessmentDetails?.user?.national_id}</td>
                    </tr>
                    <tr>
                      <th>{t("Nationality")}</th>
                      <td>{assessmentDetails?.user?.nationality_name}</td>
                    </tr>
                    <tr>
                      <th>
                        {t(
                          "Have you ever worked in crowd management at the Holy Train of the Sacred Sites?"
                        )}
                      </th>
                      <td>
                        {assessmentDetails?.user?.has_crowd_management_exp
                          ? t("Yes")
                          : t("No")}
                      </td>
                    </tr>
                    {assessmentDetails?.user?.has_crowd_management_exp && (
                      <tr>
                        <th>{t("Crowd Management Experience")}</th>
                        <td>
                          {assessmentDetails?.user?.crowd_management_exp?.map(
                            item => (
                              <div
                                key={item?.job}
                                className="d-flex align-items-center justify-content-between gap-2"
                              >
                                <h6 className="fit_content">
                                  {item?.job_title}
                                </h6>
                                <h6 className="font-bold fit_content">
                                  {item?.station_title}
                                </h6>
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <div className="info">
                  <button className="theme_btn w-100" onClick={toggle}>
                    {t("All Information")}
                  </button>
                  {/* <div className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      id="profileAccepted"
                      checked={is_profile_accepted}
                    />
                    <label htmlFor="profileAccepted" className="ms-2 mb-0">
                      {t("Is the candidate photo acceptable?")}
                    </label>
                  </div> */}
                </div>
              </div>
            </div>
            <div className="right">
              <div className="questions_container">
                {questions?.map((category, catIndex) => (
                  <div className="q_single" key={catIndex}>
                    <h3 className="title">{category.qn_category}</h3>
                    {category?.questions?.map((question, qIndex) => (
                      <div className="question" key={question.id}>
                        <div className={`q_left ${question.type}`}>
                          <h6>{question.question}</h6>
                        </div>
                        <div className="q_right">
                          {question.type === "option" ? (
                            <div className="button_group">
                              {question?.options?.map(option => (
                                <button
                                  key={option.id}
                                  className={`theme_btn ${
                                    activeButtons[question.id] === option.id
                                      ? "active"
                                      : ""
                                  }`}
                                  disabled
                                >
                                  {option.option}
                                </button>
                              ))}
                            </div>
                          ) : (
                            ""
                            // <textarea
                            //   value={textInputs[question.id] || ""}
                            //   onChange={e =>
                            //     handleInputChange(question.id, e.target.value)
                            //   }
                            // />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="q_single">
                  <h3 className="title">{t("General recommendation")}</h3>
                  {/* Is the candidate suitable for the job */}
                  <div className="question">
                    <div className="q_left">
                      <h6>{t("Is the candidate suitable for the job?")}</h6>
                    </div>
                    <div className="q_right">
                      <div className="button_group">
                        {["Yes", "No"].map(option => (
                          <button
                            key={option}
                            className={`theme_btn ${
                              suitableForJob === option ? "active" : ""
                            }`}
                            disabled
                          >
                            {t(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Reason - Show only when "No" is selected */}
                  {suitableForJob === "No" && (
                    <div className="question">
                      <div className="q_left">
                        <h6>{t("Reason")}</h6>
                      </div>
                      <div className="q_right">
                        <div className="button_group">
                          {[
                            "Other",
                            "Nationality does not match",
                            "Age does not match",
                          ].map(option => (
                            <button
                              key={option}
                              className={`theme_btn ${
                                reason === option ? "active" : ""
                              }`}
                              disabled
                            >
                              {t(option)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Job nominated - Show only when "Yes" is selected */}
                  {suitableForJob === "Yes" && (
                    <div className="question">
                      <div className="q_left">
                        <h6>{t("Job nominated")}</h6>
                      </div>
                      <div className="q_right">
                        <div className="button_group">
                          {[
                            "Station Staff",
                            "Waiting Area and Access Control",
                          ].map(option => (
                            <button
                              key={option}
                              className={`theme_btn ${
                                jobNominated === option ? "active" : ""
                              }`}
                              disabled
                            >
                              {t(option)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Is he nominated for a leadership position? - Show only when "Yes" is selected */}
                  {suitableForJob === "Yes" && (
                    <div className="question">
                      <div className="q_left">
                        <h6>
                          {t("Is he nominated for a leadership position?")}
                        </h6>
                      </div>
                      <div className="q_right">
                        <div className="button_group">
                          {["No", "Yes"].map(option => (
                            <button
                              key={option}
                              disabled
                              className={`theme_btn ${
                                leadershipPosition === option ? "active" : ""
                              }`}
                            >
                              {t(option)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Proposed position - Show only when "Job nominated" is "Entrance gates and waiting area" */}
                  {(jobNominated === "Entrance gates and waiting area" ||
                    jobNominated === "Station Staff") && (
                    <div className="question">
                      <div className="q_left">
                        <h6>{t("Proposed position")}</h6>
                      </div>
                      <div className="q_right">
                        <select disabled value={proposedPosition || ""}>
                          <option value="" disabled>
                            {t("Select an option")}
                          </option>
                          {jobTitlesList
                            ?.filter(job => {
                              if (showAll) return true

                              if (jobIds.includes(job?.id)) return true
                              return false
                            })
                            .map(item => (
                              <option value={item?.id} key={item?.id}>
                                {getLocalizedName(item, localizeLanguage)}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Reviews */}
                  {(suitableForJob === "Yes" ||
                    (suitableForJob === "No" && reason === "Other")) && (
                    <div className="question">
                      <div className="q_left">
                        <h6>{t("Reviews")}</h6>
                      </div>
                      <div className="q_right">
                        <textarea value={review} disabled />
                      </div>
                    </div>
                  )}

                  {/* Would you like to work  */}
                  <div className="question">
                    <div className="q_left">
                      <h6>
                        {t("Would you like to work during trial operation?")}
                      </h6>
                    </div>
                    <div className="q_right">
                      <div className="button_group">
                        {["Yes", "No"].map(option => (
                          <button
                            key={option}
                            className={`theme_btn ${
                              would_you_like_work_in_trial_period ===
                              (option === "Yes")
                                ? "active"
                                : ""
                            }`}
                            onClick={() =>
                              setWould_you_like_work_in_trial_period(
                                option === "Yes"
                              )
                            }
                            disabled
                          >
                            {t(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Language */}
                  <div className="question">
                    <div className="q_left">
                      <h6>{t("Select Language")}</h6>
                    </div>
                    <div className="q_right">
                      <div className="button_group">
                        {["Arabic", "English"].map(option => (
                          <button
                            key={option}
                            className={`theme_btn ${
                              language === option.toLowerCase() ? "active" : ""
                            }`}
                            onClick={() => setLanguage(option.toLowerCase())}
                            disabled
                          >
                            {t(option)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Candidate eligible for a promotion */}

                  {selectedJob?.is_available_for_promotion &&
                    (showAll ||
                      permissions.includes(
                        "can_candidate_promote_for_job"
                      )) && (
                      <div className="question">
                        <div className="q_left">
                          <h6>
                            {t("Is the candidate eligible for a promotion?")}
                          </h6>
                        </div>
                        <div className="q_right">
                          <div className="button_group">
                            {["Yes", "No"].map(option => (
                              <button
                                key={t(option)}
                                className={`theme_btn ${
                                  is_eligible_for_promotion === option
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() => {
                                  setEligible_for_promoted(option)
                                }}
                                disabled
                              >
                                {t(option)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* If select Yes for candidate promote then show */}
                  {/* Proposed position - Show only when "Job nominated" is "Entrance gates and waiting area" */}
                  {/* {is_eligible_for_promotion === "Yes" && (
                    <div className="question">
                      <div className="q_left">
                        <h6>{t("Select promoted position for candidate")}</h6>
                      </div>
                      <div className="q_right">
                        <select
                          value={promotion_job_title_id || ""}
                          onChange={e =>
                            setPromotion_job_title_id(e.target.value)
                          }
                          disabled
                        >
                          <option value="" disabled>
                            {t("Select an option")}
                          </option>
                          {jobTitlesList?.map(item => (
                            <option value={item?.id} key={item?.id}>
                              {item?.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </Container>
      )}
      <Modal isOpen={modal} toggle={toggle} size="xl">
        <ModalHeader toggle={toggle}>{t("Candidate Details")}</ModalHeader>
        <ModalBody>
          <CandidateDetails
            uuid={jobApplicationDetails?.user_information?.uuid}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ViewAssessment
